import type { Direction, Status } from "./directionStatus";

const STATUS_DOT_CLASS: Record<Status, string> = {
  ok: "bg-green-500",
  warn: "bg-yellow-500",
  err: "bg-red-500",
};

const STATUS_LABEL: Record<Status, string> = {
  ok: "OK",
  warn: "Warning",
  err: "Error",
};

export interface DirectionStatusCellProps {
  direction: Direction;
  status: Status;
  /** Hide the BUY/SELL badge (e.g. the watchlist symbol cell only wants the dot). */
  showDirection?: boolean;
  className?: string;
}

/**
 * Unified blotter indicator (proposal 13): a status dot plus a fixed-size
 * BUY/SELL badge tinted on the price palette (blue-400 / red-400). Rendered
 * identically across watchlist, open positions and history.
 *
 * The "ok" (green) dot is intentionally NOT rendered — a healthy open position
 * needs no extra marker next to its BUY/SELL badge, and the green dots read as
 * noise. Only the actionable warn (yellow) / err (red) states show a dot.
 */
export function DirectionStatusCell({
  direction,
  status,
  showDirection = true,
  className,
}: DirectionStatusCellProps) {
  const isBuy = direction === "buy";
  const showDot = status !== "ok";
  return (
    <span
      className={`inline-flex items-center gap-1.5${className ? ` ${className}` : ""}`}
    >
      {showDot && (
        <span
          className={`inline-block h-[7px] w-[7px] shrink-0 rounded-full ${STATUS_DOT_CLASS[status]}`}
          role="img"
          aria-label={STATUS_LABEL[status]}
        />
      )}
      {showDirection && (
        <span
          className={`inline-flex h-[15px] w-[38px] shrink-0 items-center justify-center rounded-sm border font-mono text-[9.5px] font-bold tracking-wide tabular-nums ${
            isBuy
              ? "border-blue-400/35 bg-blue-400/10 text-blue-400"
              : "border-red-400/35 bg-red-400/10 text-red-400"
          }`}
        >
          {isBuy ? "BUY" : "SELL"}
        </span>
      )}
    </span>
  );
}
